"use client";

import { debounce } from "es-toolkit";
import { useEffect, useMemo, useRef } from "react";
import type { ImperativePanelGroupHandle } from "react-resizable-panels";
import {
  getSidebarLayoutCookie,
  setSidebarLayoutCookie,
} from "@/lib/cookie/cookie-client";
import { normalizeTwoPanelLayout } from "@/lib/panel-layout";

const AI_SIDE_PANEL_SIZE_KEY = "ai-side-panel-size";

export const SIDEBAR_MIN_SIZE = 13;
export const AI_PANEL_MIN_SIZE = 25;
export const AI_PANEL_MAX_SIZE = 50;
export const AI_PANEL_DEFAULT_SIZE = 30;

const DEFAULT_LAYOUT: [number, number] = [20, 80];

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const getValidatedLayout = (
  layout: number[] | null,
  fallback: [number, number],
): [number, number] => {
  if (!layout) return fallback;
  return normalizeTwoPanelLayout(layout) ?? fallback;
};

const readAiPanelSize = (): number | null => {
  if (typeof window === "undefined") return null;
  const raw = localStorage.getItem(AI_SIDE_PANEL_SIZE_KEY);
  if (!raw) return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
};

const writeAiPanelSize = (value: number) => {
  if (typeof window === "undefined") return;
  localStorage.setItem(AI_SIDE_PANEL_SIZE_KEY, String(value));
};

const clampAiPanelSize = (value: number, sidebarSize: number) => {
  const maxAi = Math.min(AI_PANEL_MAX_SIZE, Math.max(0, 100 - sidebarSize));
  if (maxAi < AI_PANEL_MIN_SIZE) {
    return maxAi;
  }
  return clamp(value, AI_PANEL_MIN_SIZE, maxAi);
};

const getSidebarSize = (
  layout: [number, number],
  isSidebarOpen: boolean,
  isAiPanelOpen: boolean,
) => {
  if (!isSidebarOpen) return 0;
  const maxSidebar = isAiPanelOpen ? 100 - AI_PANEL_MIN_SIZE : 100;
  return clamp(layout[0], SIDEBAR_MIN_SIZE, maxSidebar);
};

const buildLayout = ({
  isSidebarOpen,
  isAiPanelOpen,
  sidebarLayout,
  aiPanelSize,
}: {
  isSidebarOpen: boolean;
  isAiPanelOpen: boolean;
  sidebarLayout: [number, number];
  aiPanelSize: number;
}) => {
  const sidebarSize = getSidebarSize(
    sidebarLayout,
    isSidebarOpen,
    isAiPanelOpen,
  );

  if (!isAiPanelOpen) {
    return [sidebarSize, Math.max(0, 100 - sidebarSize), 0];
  }

  const aiSize = clampAiPanelSize(aiPanelSize, sidebarSize);
  return [sidebarSize, Math.max(0, 100 - sidebarSize - aiSize), aiSize];
};

type WorkspacePanelLayoutOptions = {
  defaultLayout: number[];
  isSidebarOpen: boolean;
  isAiPanelOpen: boolean;
};

export function useWorkspacePanelLayout({
  defaultLayout,
  isSidebarOpen,
  isAiPanelOpen,
}: WorkspacePanelLayoutOptions) {
  const panelGroupRef = useRef<ImperativePanelGroupHandle>(null);

  const normalizedDefaultLayout = useMemo(
    () => normalizeTwoPanelLayout(defaultLayout) ?? DEFAULT_LAYOUT,
    [defaultLayout],
  );

  const sidebarLayoutRef = useRef<[number, number]>(normalizedDefaultLayout);
  const aiPanelSizeRef = useRef<number>(
    clampAiPanelSize(AI_PANEL_DEFAULT_SIZE, normalizedDefaultLayout[0]),
  );

  const isSidebarOpenRef = useRef(isSidebarOpen);
  const isAiPanelOpenRef = useRef(isAiPanelOpen);

  useEffect(() => {
    isSidebarOpenRef.current = isSidebarOpen;
  }, [isSidebarOpen]);

  useEffect(() => {
    isAiPanelOpenRef.current = isAiPanelOpen;
  }, [isAiPanelOpen]);

  useEffect(() => {
    const nextSidebarLayout = getValidatedLayout(
      getSidebarLayoutCookie(),
      normalizedDefaultLayout,
    );
    sidebarLayoutRef.current = nextSidebarLayout;

    const storedSize = readAiPanelSize();
    aiPanelSizeRef.current = clampAiPanelSize(
      storedSize ?? AI_PANEL_DEFAULT_SIZE,
      nextSidebarLayout[0],
    );

    const nextLayout = buildLayout({
      isSidebarOpen,
      isAiPanelOpen,
      sidebarLayout: sidebarLayoutRef.current,
      aiPanelSize: aiPanelSizeRef.current,
    });
    panelGroupRef.current?.setLayout(nextLayout);
  }, [isSidebarOpen, isAiPanelOpen, normalizedDefaultLayout]);

  const onLayout = useMemo(
    () =>
      debounce((sizes: number[]) => {
        const sidebarOpen = isSidebarOpenRef.current;
        const aiOpen = isAiPanelOpenRef.current;

        if (sidebarOpen && sizes.length >= 2) {
          const sidebarSize = sizes[0] ?? 0;
          const normalized = normalizeTwoPanelLayout([
            sidebarSize,
            Math.max(0, 100 - sidebarSize),
          ]);
          if (normalized) {
            setSidebarLayoutCookie(normalized);
            sidebarLayoutRef.current = normalized;
          }
        }

        if (aiOpen && sizes.length === 3) {
          const sidebarSize = sizes[0] ?? 0;
          const aiSize = sizes[2];
          if (typeof aiSize === "number" && Number.isFinite(aiSize)) {
            const clamped = clampAiPanelSize(aiSize, sidebarSize);
            aiPanelSizeRef.current = clamped;
            writeAiPanelSize(clamped);
          }
        }
      }, 200),
    [],
  );

  useEffect(() => {
    return () => {
      onLayout.cancel();
    };
  }, [onLayout]);

  const layout = buildLayout({
    isSidebarOpen,
    isAiPanelOpen,
    sidebarLayout: sidebarLayoutRef.current,
    aiPanelSize: aiPanelSizeRef.current,
  });

  return { panelGroupRef, layout, onLayout };
}
